/**
 * @fileoverview Legend in the footer of the Task Tracker card (AR-912 / AR-924), rendered with the same
 * icon/color the cells use so it can never drift from the matrix.
 */

import { Box, Group, ThemeIcon, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { STATUS_COLOR, STATUS_ICON } from './TableCells'
import { TeacherUploadMark } from './TeacherUploadMark'
import type { TrackStatus } from '../types'

/** Display order — matches the lifecycle of a cell. */
const LEGEND_STATUSES: TrackStatus[] = ['pending', 'in_progress', 'completed']

const LEGEND_LABEL_KEY: Record<TrackStatus, string> = {
  pending: 'task_tracker.legend.pending',
  in_progress: 'task_tracker.legend.in_progress',
  completed: 'task_tracker.legend.completed',
}

/** Figma: 28px icon boxes, 24px between entries, 8px between icon and label, 14px labels. */
const ENTRY_SPACING = 'lg'
const ICON_LABEL_SPACING = 'xs'
const LEGEND_ICON_SIZE = 'md'

export function TaskTrackerLegend() {
  const { t } = useTranslation()

  return (
    <Group spacing={ENTRY_SPACING} role="list" aria-label={t('task_tracker.legend.title')}>
      {LEGEND_STATUSES.map(status => (
        <Group key={status} spacing={ICON_LABEL_SPACING} noWrap role="listitem">
          <ThemeIcon variant="light" color={STATUS_COLOR[status]} size={LEGEND_ICON_SIZE} radius="sm">
            {STATUS_ICON[status]}
          </ThemeIcon>
          <Text size="sm" color="dimmed">
            {t(LEGEND_LABEL_KEY[status])}
          </Text>
        </Group>
      ))}
      <Group spacing={ICON_LABEL_SPACING} noWrap role="listitem">
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <ThemeIcon variant="light" color={STATUS_COLOR.completed} size={LEGEND_ICON_SIZE} radius="sm">
            {STATUS_ICON.completed}
          </ThemeIcon>
          <TeacherUploadMark />
        </Box>
        <Text size="sm" color="dimmed">
          {t('task_tracker.legend.uploaded_by_teacher')}
        </Text>
      </Group>
    </Group>
  )
}
